import { SceneEmptyState } from './scene-editors/sceneEmptyState';
import ArrowUpwardIcon from 'src/shared/material_icons/navigation/arrow_upward.svg';
import ArrowDownwardIcon from 'src/shared/material_icons/navigation/arrow_downward.svg';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import OverlayPanel from './overlayPanel';
import CommandMenu from './scene-editors/commandMenu';
import { SceneEditorAction } from './scene-editors/sceneEditorActionBar';
import { EmptyNodeIcon, EyeMixedIcon, ObjectsIcon } from '../../../../widgets/icons/scene';
import LockIcon from 'src/shared/material_icons/action/lock.svg';
import LockOpenIcon from 'src/shared/material_icons/action/lock_open.svg';
import EyeIcon from 'src/shared/material_icons/action/visibility.svg';
import EyeOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import { carriedFrom, startPointerReorder } from './pointerReorder';
import { panel } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { stringFormat, T } from '../../../../../shared/helpers';
import { useInlineRename } from './useInlineRename';
import { treeRowRing, treeRowWash, treeTrailInk } from './treeRowLook';
import { ZTooltip } from '../../../../widgets/ztooltip';

// Un contenitore è un Empty, non una cartella: una trasformazione, un'origine e
// tre assi. Aperto o chiuso non cambia cosa è — a dirlo è la freccia della riga —
// quindi il glifo è lo stesso, come nel prototipo.
const ICONS = {
    folderClosed: <EmptyNodeIcon />,
    folderOpen: <EmptyNodeIcon />,
    // L'oggetto porta il glifo del rail Objects, lo stesso della testata del suo Inspector.
    file: <ObjectsIcon />,
    eye: <EyeIcon />,
    eyeOff: <EyeOffIcon />,
    eyeMixed: <EyeMixedIcon />,
    locked: <LockIcon />,
    unlocked: <LockOpenIcon />
};

/**
 * LA VISIBILITÀ DI UN GRUPPO NON È SUA: è il riassunto di quello che contiene. Un contenitore
 * non è una cosa che si vede — è il posto dove stanno le cose che si vedono — quindi il suo
 * occhio legge i figli e li comanda tutti insieme.
 *
 * `some` è uno stato vero e va detto: dentro un gruppo è normale averne spento uno solo, e un
 * occhio acceso su quel gruppo direbbe una cosa falsa.
 */
const leavesOf = (node: any): any[] => (node.children?.length ? node.children.flatMap((child: any) => leavesOf(child)) : [node]);

// NASCONDERE UN RAMO È NASCONDERLO TUTTO, contenitore compreso. Prendendo solo le foglie, un
// contenitore che ha anche una geometria sua — nei modelli importati capita — restava a schermo
// mentre l'occhio diceva che il ramo era spento. Il lucchetto invece resta sulle foglie: bloccare
// è togliere la pescabilità, e un nodo senza superficie non si pesca comunque.
const branchOf = (node: any): any[] => [node, ...(node.children ?? []).flatMap((child: any) => branchOf(child))];

const groupState = (nodes: any[], isOn: (node: any) => boolean): 'all' | 'some' | 'none' => {
    const on = nodes.filter(isOn).length;

    return on === nodes.length ? 'all' : on === 0 ? 'none' : 'some';
};

const isShown = (leaf: any) => leaf.visible !== false;
const isShut = (leaf: any) => leaf.locked === true;

/**
 * UNA FABBRICA, NON UNA FOTOGRAFIA. Chi costruisce l'albero può consegnare il pannello di una riga
 * come funzione, e quella funzione vuol dire «chiedimelo quando ti serve»: il momento in cui serve
 * è ogni ridisegno, perché fra l'uno e l'altro le cose si muovono da sole — il gizmo trascina
 * l'oggetto in scena e il motore non lo dice a nessuno.
 *
 * Chiamandola una volta sola, al clic, l'Inspector si teneva i numeri di quell'istante: spostavi
 * l'oggetto a schermo e i campi restavano dov'erano, come due strumenti che misurano cose diverse.
 */
const LiveComponent = ({ render }: { render: () => React.ReactNode }) => <>{render()}</>;

// Il pannello di una riga esce da qui e da nessun altro posto: chi lo consegna a mano, prima o poi,
// ne consegna uno solo dei due tipi e la colonna di destra torna a invecchiare.
const publishable = (component: any): React.ReactNode => (typeof component === 'function' ? <LiveComponent render={component} /> : component);

/** Sei fasi da 180ms — acceso e spento tre volte. Lo legge sia il timer sia la durata di una fase. */
const BLINK_DURATION = 1080;

// LA FRECCETTA DI UN RAMO, ed è esportata perché la usa anche la vista a GRIGLIA dei materiali:
// là un rientro non esiste, e aprire o chiudere un gruppo è l'unico modo che la griglia ha di
// mostrarlo come una cosa sola. Due triangoli disegnati due volte sarebbero due cose da tenere
// LO STESSO BOTTONE DEI COMANDI DI RIGA, col glifo Material del Composer: due disegni diversi per
// lo stesso gesto — aprire un ramo — si leggono come due gesti diversi.
// LA FRECCETTA DEL PROTOTIPO (utente, 2026-10-20): sottile, grigio chiaro, una sola che gira di 90° aprendo il ramo.
const TwistyButton = styled.button<{ $isOpen: boolean; $trail?: boolean }>`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    /* STRETTA E SENZA UN PASSAGGIO SUO (utente, 2026-10-15), come nel Composer: si accende la riga, non il
       tasto, e a sinistra della freccetta non resta un tasto intero di vuoto. */
    /* IL BERSAGLIO È LARGO 24 (utente, 2026-10-22, WCAG 2.5.8), ma nella riga ne occupa 16: i margini negativi
       tengono la freccetta dov'era. */
    width: 24px;
    height: 24px;
    margin: 0 -4px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    /* Sulla riga che contiene la scelta la freccetta prende l'arancio, come il glifo (treeRowLook). */
    color: ${(props) => (props.$trail ? treeTrailInk(props.theme) : 'var(--editor-faint, #676a6e)')};
    cursor: pointer;
    user-select: none;

    svg {
        width: 14px;
        height: 14px;
        transform: rotate(${(props) => (props.$isOpen ? '90deg' : '0deg')});
        transition: transform 200ms ease;
    }

    /* Vale anche qui: il glifo a contorno tiene il suo tratto, quello Material si riempie. */
    svg:not([stroke]) {
        fill: currentColor;
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 1px;
    }
`;

export const TreeTwisty: React.FC<React.ComponentProps<typeof TwistyButton>> = (props) => (
    <TwistyButton {...props}>
        <ChevronRightIcon />
    </TwistyButton>
);

/* Il posto vuoto di una riga senza rami, così i nomi restano incolonnati. */
export const TreeTwistySlot = styled.span`
    display: inline-flex;
    flex: none;
    width: 16px;
    height: 24px;
`;

// IL FONDO DI UNA RIGA HA UNA SORGENTE SOLA, perché il lampeggio deve saper tornare esattamente
// dov'era: scritto due volte, la riga finirebbe di lampeggiare su un colore diverso da quello che
// aveva prima.
//
// LA SCIA HA LO STESSO LAVATO DELLA RIGA SCELTA (utente, 2026-10-15; prototipo `.row.trail`): scegliere
// un figlio non spegne i padri, si vede ancora dove sta. A distinguerle è il filo, che ha solo la scelta.
const rowBackground = (props: { $selected: boolean; $trail?: boolean; theme: any }) =>
    props.$selected || props.$trail ? treeRowWash(props.theme) : 'transparent';

// IL LAMPEGGIO — «è questa». Sei fasi da 180ms: acceso e spento tre volte, che è quel tanto che
// l'occhio segue senza doverlo cercare.
//
// SPENTO È TRASPARENTE, e non il fondo che la riga aveva. Alternare fra il lavato della riga
// scelta e uno appena più carico non si vedeva: su una riga già accesa — e quando si chiede
// «dove sta?» la riga è quasi sempre quella scelta — i due toni erano la stessa cosa. Perdere e
// riprendere il fondo è il segno che si legge da un metro, ed è anche ciò che fa il prototipo.
const blinkRow = keyframes`
    0%, 49.9% { background: var(--row-blink-hot); }
    50%, 100% { background: transparent; }
`;

const TreeNodeContainer = styled.div<{
    $depth: number;
    $selected: boolean;
    $trail?: boolean;
    $isVisible: boolean;
    $blinking?: boolean;
    $smallText?: boolean;
}>`
    cursor: pointer;
    display: flex;
    align-items: center;

    // L'ALTEZZA DELLA RIGA È DICHIARATA, non dedotta dal glifo che ci capita dentro. Dedotta,
    // valeva ventiquattro pixel — nome, icona e occhio incollati — e un albero di venti mesh
    // diventava un blocco di testo in cui le righe si contano solo rileggendole. Trentaquattro è
    // la misura con cui una riga di elenco si distingue dalla vicina senza che serva un filo a
    // dividerle, ed è anche il pavimento comodo per un bersaglio da colpire col puntatore.
    position: relative;
    min-height: 30px;
    // DUE PIXEL FRA UNA RIGA E L'ALTRA (prototipo, .insp-rows gap --s1; utente 2026-10-21): due righe accese
    // vicine restano due, e l'albero respira senza fili.
    margin-bottom: 2px;
    // Le stesse misure delle righe del Composer: pastiglia con sei pixel di respiro ai lati.
    padding: 0 6px;
    border-radius: 8px;
    // Declared, not inherited from the page: the Composer rows carry the same 13px, so the two
    // editors read as one tree. Inheriting gave 14px here and nothing matched the panel around it.
    font-size: ${(props) => (props.$smallText ? '12px' : '13px')};
    // IL RIENTRO SI SCRIVE QUI E BASTA. Lo scriveva anche ChildrenContainer, che è
    // annidato: i due si sommavano a ogni livello, e a profondità quattro il nome del
    // nodo finiva oltre il bordo destro del pannello. Restavano l'occhio e il glifo, e
    // sembrava che le mesh non avessero un nome.
    // Quattordici e non venti: il pannello è largo poco più di duecento pixel, e il
    // rientro è un'informazione — non deve costare più della cosa che indenta.
    padding-left: ${(props) => props.$depth * 15 + 6}px;
    --row-blink-hot: color-mix(in srgb, var(--editor-accent-brand, #e8650a) 40%, transparent);

    background: ${rowBackground};

    /* NASCOSTA, SI SBIADISCE SOLO IL NOME (prototipo): l'icona e i comandi restano leggibili, e l'occhio chiuso lo dice. */
    ${(props) => !props.$isVisible && `& ${NodeName} { opacity: 0.45; }`}

    // LA RIGA SCELTA HA UN FILO ARANCIO INTORNO (utente, 2026-10-15; prototipo .row.selected): è
    // ciò che la separa dalla scia, che ha lo stesso fondo. Prima era un trattino a sinistra, e un
    // padre acceso con un figlio acceso non diceva quale dei due fosse la scelta.
    ${(props) => props.$selected && `box-shadow: ${treeRowRing(props.theme)};`}

    // Il passaggio del puntatore non spegne la scelta né la scia.
    &:hover {
        background: ${(props) => (props.$selected || props.$trail ? treeRowWash(props.theme) : 'var(--editor-field, #f1f2f4)')};
    }

    ${(props) =>
        props.$blinking &&
        css`
            animation: ${blinkRow} 360ms steps(1, end) 3;
        `}
`;

const TreeNodeInnerContainer = styled.div`
    /* L'arancio del marchio per la linea e l'ospite del trascinamento (pointerReorder). */
    --reorder-accent: ${(props) => props.theme.app.secondary};
    min-height: 100%;
    display: flex;
    flex-direction: column;
    /* Dalla ricerca alla prima riga dieci pixel in tutto, coi sei del bordo della ricerca (prototipo: 8 + 2). */
    & > :first-child {
        margin-top: 4px;
    }
`;

// L'INTESTAZIONE FRA DUE GRUPPI DI RIGHE (prototipo, .items-band): maiuscoletto grigio, con il respiro di una sezione.
const TreeBand = styled.p`
    margin: 10px 0 4px;
    padding: 0 6px;
    color: var(--editor-faint, #676a6e);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

// Raggruppa i figli e non li rientra: il rientro lo scrive la riga, che conosce la
// propria profondità. Qui sarebbe cumulativo, perché questi contenitori si annidano.
const ChildrenContainer = styled.div`
    min-width: 0;
`;

// I GLIFI SI MISURANO QUI. Gli SVG della scena non portano `width`/`height` — se li
// portassero, SVGO toglierebbe il `viewBox` e la figura verrebbe disegnata a 1:1 dentro
// un riquadro di sedici pixel. Chi li ospita deve quindi dargli una misura: senza, in
// una riga flex l'immagine non ha larghezza intrinseca, viene compressa a zero e la
// riga sembra semplicemente priva di icona. Da qui anche `flex: none`.
const glyphBox = (size: number) => `
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    margin-right: 4px;
    user-select: none;

    svg {
        width: ${size}px;
        height: ${size}px;
        display: block;
    }
`;

// LA SPECIE COLORA IL SUO GLIFO: verde-acqua il soggetto, viola il raggruppamento. È un tratto di
// CIÒ CHE LA RIGA È, non del suo stato — per questo non cede all'arancio della selezione, che dice
// un'altra cosa («questa è scelta»), e che la riga presa porta già addosso in altri due modi.
//
// CHI LA SPECIE NON LA DICHIARA RESTA COM'ERA: le liste che non distinguono — le camere — non
// hanno una domanda a cui il colore risponda, e un colore che dice «questo è un oggetto» su una
// camera direbbe una cosa falsa.
// SULLA SCIA IL GLIFO PRENDE L'ARANCIO (prototipo `.row.trail .row-icon`): lì non dice la specie, dice
// «la scelta sta qui dentro».
const Icon = styled.span<{ $kind?: string; $trail?: boolean }>`
    ${glyphBox(16)}
    color: ${(props) =>
        props.$trail ? treeTrailInk(props.theme) : props.$kind === 'group' ? panel.kindGroupInk : props.$kind === 'mesh' ? panel.kindSubjectInk : 'inherit'};

    /* I glifi Material si riempiono, quelli della scena sono a contorno. */
    svg:not([stroke]) {
        fill: currentColor;
    }
`;
// UN GLIFO DATO DA CHI COSTRUISCE L'ALBERO (la cartella dei materiali): grigio, e arancio sulla scia come gli altri
// (prototipo, .row.trail .row-icon; utente 2026-10-21). Chi lo vuole del suo colore lo scrive sul glifo.
const CustomIconContainer = styled.div<{ $trail?: boolean }>`
    ${glyphBox(18)}
    color: ${(props) => (props.$trail ? treeTrailInk(props.theme) : 'var(--editor-muted, #55595f)')};

    svg:not([stroke]) {
        fill: currentColor;
    }
`;
// I COMANDI DI RIGA SONO BOTTONI, non glifi sbiaditi: stessa misura e stesso fondo al passaggio
// dei comandi del Composer, così i due alberi si usano allo stesso modo.
const VisibilityIcon = styled.span<{ $mixed?: boolean }>`
    display: inline-flex;
    flex: none;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    margin-right: 0;
    border-radius: 4px;
    cursor: pointer;
    /* Uno stato acceso è arancio (l'accent), come negli altri comandi di riga (ricetta «comando di riga»). */
    color: ${(props) => (props.$mixed ? 'var(--editor-accent, #a94700)' : 'inherit')};

    svg {
        width: 14px;
        height: 14px;
        display: block;
    }

    /* I glifi Material si riempiono, quelli della scena sono disegnati a contorno: riempirli
       cancellava il tratto e l'occhio «misto» diventava una macchia arancione. */
    svg:not([stroke]) {
        fill: currentColor;
    }

    &:hover {
        background: var(--editor-sunken, #e9ebed);
    }
`;

// ACCESO SI VEDE. È lo stesso bottone dell'occhio, ma dichiara uno stato che in tutto l'elenco
// vale per una riga sola — stai guardando ATTRAVERSO quella camera — e uno stato unico va letto
// senza cercarlo: prende la tinta d'azione e resta acceso anche quando il mouse è altrove.
const RowToggleIcon = styled(VisibilityIcon)<{ $on: boolean }>`
    opacity: ${(props) => (props.$on ? 1 : 0.72)};
    color: ${(props) => (props.$on ? 'var(--editor-accent, #a94700)' : 'inherit')};
`;
const SearchHit = styled.mark`
    background: color-mix(in srgb, var(--editor-accent-brand, #e8650a) 27%, transparent);
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
`;

/**
 * ISOLARE DICE QUALI RIGHE, NON PERCHÉ. Con l'elenco ridotto a tre righe su ottanta la domanda
 * diventa subito la seconda — «e adesso quale pezzo di questo nome ho scritto io?» — e la si
 * risponde rileggendo, che è esattamente il lavoro che la ricerca doveva togliere.
 *
 * SI CERCA ANCHE PER COSE CHE NEL NOME NON CI SONO: negli oggetti la ricerca guarda pure il
 * materiale. Lì il nome non contiene niente da accendere, e infatti non si accende nulla — la
 * riga c'è perché ha corrisposto altrove, e dirlo con un colore inventato sarebbe una bugia.
 */
export const markMatches = (text: string, query?: string): React.ReactNode => {
    const needle = query?.trim().toLowerCase();
    if (!needle) return text;

    const hay = text.toLowerCase();
    const pieces: React.ReactNode[] = [];
    let from = 0;
    let at = hay.indexOf(needle);

    while (at !== -1) {
        if (at > from) pieces.push(text.slice(from, at));
        pieces.push(<SearchHit key={at}>{text.slice(at, at + needle.length)}</SearchHit>);
        from = at + needle.length;
        at = hay.indexOf(needle, from);
    }

    if (!pieces.length) return text;
    if (from < text.length) pieces.push(text.slice(from));

    return pieces;
};

// CHI CONTIENE SI LEGGE PIÙ FORTE DI CHI STA DENTRO. In un albero profondo la struttura la
// disegnano i rientri, ma i rientri si contano solo se si guarda il bordo sinistro: il grassetto
// dice a colpo d'occhio dove comincia un ramo, e lo dice al centro della riga, dove si sta già
// leggendo il nome.
const NodeName = styled.span<{ $branch?: boolean }>`
    user-select: none;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    /* Peso normale anche per i rami, come il prototipo (utente, 2026-10-15): lo dicono freccetta e glifo. */
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// IL NOME SI CAMBIA DOVE SI LEGGE, come nella testata dell'Inspector: nessuna icona, nessuna
// finestra. Il gesto è quello di ogni gestore di file — si sceglie la riga, e un secondo clic sul
// nome lo apre — e vale sia per una riga sia per una cartella.
//
// IL CAMPO EREDITA IL CARATTERE DELLA RIGA e restituisce lo spazio della propria cornice, o il
// nome si sposterebbe di qualche pixel nel momento in cui lo si tocca: un testo che salta mentre
// lo stai per scrivere fa dubitare di aver cliccato la cosa giusta.
// UN CONTORNO SOLO (utente, 2026-10-21): la riga scelta ha già il suo filo arancio, e il campo ci stava dentro con un
// bordo arancio e il fuoco da tastiera — tre linee una dentro l'altra. Il campo è un riquadro bianco col filo grigio,
// e il testo scelto prende l'arancio tenue invece del blu del browser.
const NodeNameInput = styled.input`
    flex: 1;
    min-width: 0;
    margin: -2px -5px;
    padding: 1px 4px;
    border: 0;
    border-radius: 4px;
    background: var(--editor-panel, #ffffff);
    box-shadow: inset 0 0 0 1px var(--editor-line, #d1d3d8);
    color: ${panel.ink};
    font: inherit;
    outline: none;

    &&:focus-visible {
        outline: none;
    }

    &::selection {
        background: color-mix(in srgb, var(--editor-accent-brand, #e8650a) 24%, transparent);
    }
`;

// IL CODINO DELLA RIGA — un dato di contorno in coda al nome: quante mesh usano un materiale,
// di che specie è una luce. Sta DOPO il nome e prima dei comandi, ed è sempre visibile: non è
// un comando che compare al passaggio, è una cosa che si legge scorrendo.
//
// UNA PAROLA STA UN GRADINO PIÙ SOTTO DI UN NUMERO. «Hemispheric» a corpo pieno spinge il nome
// fuori dalla riga, e un nome troncato per un dato di contorno rovescia la gerarchia. Il numero
// invece è corto e va incolonnato, che è il solo modo perché una colonna di numeri si legga
// come una colonna.
/** Il dato di contorno in coda al nome di una riga: un numero, o una parola. */
export type TreeRowNote = { text: string; title?: string };

const RowNote = styled.span`
    flex: none;
    margin-left: 6px;
    /* Il grigio tenue della palette, non il testo con l'opacità: a 0,65 scendeva sotto il contrasto del testo piccolo
       (utente, 2026-10-23); e 11, il più piccolo dei testi non maiuscoli. */
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

// I COMANDI DELLA RIGA STANNO IN FONDO E COMPAIONO AL PASSAGGIO. Una colonna di occhi
// accesi a sinistra di ogni nome pesa quanto i nomi e non dice niente: se tutto è visibile,
// l'informazione è zero. Qui l'occhio si mostra sulla riga che stai toccando — o su quella
// selezionata — e resta **fisso** su ciò che è nascosto, perché quello è l'unico caso in cui
// il segno porta un'informazione.
const RowTrailing = styled.span<{ $pinned: boolean }>`
    flex: none;
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 6px;
    opacity: ${(props) => (props.$pinned ? 1 : 0)};
    transition: opacity 0.12s ease;

    ${TreeNodeContainer}:hover &,
    ${TreeNodeContainer}:focus-within & {
        opacity: 1;
    }
`;

// Interfaccia per l'handle del TreeComponent
export interface TreeHandle {
    closeOverlay: () => void;
    /**
     * Toglie la scelta e chiude il pannello della riga: è il clic sul vuoto, chiesto da fuori.
     * Facoltativo perché non tutti gli handle sono questo albero — la griglia dei materiali ne
     * costruisce uno suo, e lì il vuoto non significa ancora niente.
     */
    clearSelection?: () => void;
    /**
     * LA COLONNA È PASSATA A UN ALTRO ELENCO: si dimentica la riga aperta SENZA scrivere nella colonna,
     * che adesso è di un altro. Senza, l'elenco rifaceva il pannello della sua riga a ogni cambiamento
     * dei propri dati e lo rimetteva al posto di quello nuovo. La selezione resta: è ancora vera.
     */
    releaseOverlay?: () => void;
    selectAndOpenOverlay: (nodeId: string) => void;
    /** Apre il nome di una riga in scrittura. Lo usa chi crea: la cosa nasce, e il nome si dà dov'è scritto. */
    startRename: (nodeId: string) => void;
}

export interface TreeComponentProps {
    treeState: {
        moveSelection: any;
        nodes: any[];
        openNodes: Set<string>;
        selectedIds: Set<string>;
        overlayNodeId: string | null;
        setDraggedId: (id: string | null) => void;
        flatVisibleList: any[];
        setOverlayNodeId: (id: string | null) => void;
        findNodeById: (id: string) => any;
        findParentId: (id: string) => string | null;
        findNodeByDataID: (nodes: any[], dataID: string) => any;
        toggleSelection: (id: string, shiftKey: boolean, ctrlKey: boolean, order?: string[]) => void;
        toggleNode: (id: string) => void;
        /** Apre la strada fino a una riga senza toccare i rami già aperti. */
        openPath?: (ids: string[]) => void;
        toggleVisibility: (id: string) => void;
        setSelectedIds: (ids: Set<string>) => void;
        canTurnIntoFolder?: (node: any) => boolean;
    };
    // LA RICERCA NON STA QUI. È della SEZIONE, non dell'albero: nella griglia dei materiali
    // l'albero non c'è e il campo deve restare dov'era, quindi vive nel `SectionPanel`. Lo stesso
    // vale per «seleziona tutto», che è un comando fra i comandi da quando la barra esiste.
    // Restavano dentro qui un campo, una casella e quattro prop che nessuno accendeva più.
    treeLabel?: string;
    /** Cosa dire quando la lista è vuota: il glifo della sezione e una riga. */
    emptyIcon?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    /**
     * «DOVE STA?» — la riga da portare in vista e far lampeggiare.
     *
     * ARRIVA PER PROP E NON DAL MANICO, ed è la correzione di un difetto vero: il manico è UNO
     * per quattro sezioni e chi lo lega per ultimo se lo prende, quindi una chiamata imperativa
     * poteva finire sull'albero sbagliato e non fare niente — in silenzio, che è il modo peggiore
     * di non funzionare. Una prop la riceve questo albero e nessun altro.
     *
     * IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe lo
     * stesso valore di prima e non succederebbe niente.
     */
    /**
     * La riga si dichiara in due modi, e sono due chi la chiede: dall'INTERNO della sezione si
     * conosce l'id del nodo — è la freccetta della testata — mentre da UN'ALTRA sezione si conosce
     * solo il `dataID` della cosa nel mondo 3D, perché il nodo dell'albero è una faccenda di chi
     * quell'albero ce l'ha.
     */
    blinkRequest?: { nodeId?: string; dataID?: string; token: number } | null;
    disableDragAndDrop?: boolean;
    /**
     * COME SI LASCIA UNA RIGA. `tree` (di partenza): davanti, dopo o dentro un'altra, con la linea.
     * `onto`: solo SOPRA una riga, e il senso lo decide chi ospita — i materiali lasciati su un
     * materiale si riordinano davanti a lui (`materialsEditor`), una regola che vive lì.
     */
    rowDropMode?: 'tree' | 'onto';
    onNodeMoved: (nodeId: string, targetParentId: string | null, index: number) => void;
    /** `nodeId` è l'id del nodo dell'albero e `nextVisible` lo stato che deve assumere. */
    onToggleNodeVisibility?: (nodeId: string, nextVisible: boolean) => void;
    showVisibilityIcon?: boolean; // New prop to control visibility icon display
    /** Le parole dell'occhio: «Hide»/«Show» di partenza, «Turn off»/«Turn on» per le luci (prototipo). */
    visibilityLabels?: { hide: string; show: string };
    /** ⌥CLIC SULL'OCCHIO ISOLA LA RIGA (prototipo; utente, 2026-10-20): chi ospita l'albero spegne il resto. */
    onIsolate?: (nodeId: string) => void;
    /**
     * IL LUCCHETTO: una riga bloccata non si sceglie dal palco e non si trasforma — si sceglie
     * solo da qui. Chi ospita l'albero dice se la specie che mostra può essere bloccata, e riceve
     * il gesto: lo stato lo tiene lui, perché è lui a parlare con la scena.
     */
    showLockIcon?: boolean;
    /** `nextLocked` è lo stato che la riga deve assumere, non quello che ha. */
    onToggleNodeLock?: (nodeId: string, nextLocked: boolean) => void;
    /** Il testo cercato adesso, quello con cui la sezione ha già ridotto l’elenco: qui si accende. */
    highlight?: string;
    /**
     * LA LISTA È PIATTA: lasciare una riga su una riga vuol dire **metterla lì**, non infilarla
     * dentro. Le luci sono così — una luce non contiene una luce — e prima quel gesto non faceva
     * niente del tutto: il nodo dichiarava `notTurnableIntoFolder` e il drop veniva annullato in
     * silenzio, mentre nel vecchio scene editor annidava una luce dentro l'altra, che è una cosa
     * che nella scena non significa niente.
     *
     * Con questo acceso l'albero **non tocca la struttura**: dice soltanto a chi lo ospita su
     * quale riga è caduto il trascinamento, e l'ordine nuovo lo scrive lui — dove lo sa salvare.
     * Per questo qui `onNodeMoved` riceve la riga BERSAGLIO e non un genitore.
     */
    flatList?: boolean;
    /**
     * UN'INTESTAZIONE PRIMA DI UNA RIGA DI PRIMO LIVELLO, per id (prototipo, «Not in a group» fra i gruppi di
     * materiali e i materiali sciolti). Non è una riga: non si sceglie e non si trascina.
     */
    bandBefore?: Record<string, React.ReactNode>;
    /** Righe a 12px, come gli elenchi di materiali del prototipo (.lrow .l-name); gli alberi della scena restano a 13. */
    smallText?: boolean;
    /**
     * I COMANDI CHE VOGLIONO UN SOGGETTO, riga per riga. Il soggetto lo decide l'albero e arriva
     * in `subjectIds`: la riga sola, oppure tutta la selezione quando il «…» è su una riga scelta
     * insieme ad altre (round 4, decisione 3). Il menu di una riga NON scelta resta suo.
     */
    rowActions?: (node: any, subjectIds: string[]) => SceneEditorAction[];
    /**
     * IL CODINO DI UNA RIGA, quando quella lista ne ha uno da dire. Restituire `null` per le
     * righe che non lo hanno — una cartella fra i materiali, un gruppo fra le luci — invece di
     * una stringa vuota, che occuperebbe comunque il suo spazio. `word` per un'etichetta, che sta
     * un gradino sotto un numero.
     */
    rowNote?: (node: any) => TreeRowNote | null | undefined;
    /**
     * UN SECONDO SEGNO DI STATO NELLA RIGA, per le liste che ne hanno due. L'occhio dice se la
     * riga si vede; questo dice qualcos'altro che è vero di lei sola — per una camera, se stai
     * guardando ATTRAVERSO. Sta PRIMA dell'occhio perché è il più specifico dei due, e l'ordine
     * è quello del mockup. Restituire `null` per le righe che non lo hanno.
     */
    rowToggle?: (node: any) => { icon: React.ReactNode; label: string; active: boolean; onClick: () => void } | null;
    /**
     * RINOMINARE SUL POSTO. Chi la dichiara abilita il gesto sulle proprie righe: un secondo clic
     * sul nome di una riga già scelta lo apre in scrittura. Riceve il nodo e non il solo
     * identificativo, perché una lista può doverne distinguere le specie — un gruppo di materiali
     * non si rinomina come un materiale.
     */
    onNodeRename?: (node: any, name: string) => void;
    disableOverlay?: boolean;
    onSelectedComponentChange?: (component: React.ReactNode | null) => void;
    /**
     * CERCARE APRE I RAMI. Un albero mostra i figli solo dei nodi aperti, e va bene finché si
     * naviga: si scende dove si vuole andare. Ma con una ricerca in corso quella regola nasconde
     * proprio ciò che si sta cercando — chi filtra vede restare i soli antenati, chiusi, e da fuori
     * sembra che la ricerca non trovi niente.
     *
     * Chi ospita l'albero lo accende quando un filtro è attivo: le righe rimaste sono già poche e
     * sono tutte pertinenti, quindi tenerle chiuse non risparmia niente a nessuno.
     */
    expandAll?: boolean;
}

// I comandi di una riga sono `span` con `role="button"`: Invio e Spazio li premono come un bottone vero. Prima il
// tasto saliva alla riga, che invece del comando sceglieva sé stessa.
const pressOnKey = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.click();
};

const TreeComponent = forwardRef<TreeHandle, TreeComponentProps>(
    (
        {
            treeState,
            treeLabel = T._('Scene items', 'Admin'),
            emptyIcon,
            emptyMessage,
            blinkRequest,
            disableDragAndDrop,
            rowDropMode = 'tree',
            onNodeMoved,
            onToggleNodeVisibility,
            showVisibilityIcon = true,
            visibilityLabels,
            onIsolate,
            showLockIcon,
            onToggleNodeLock,
            highlight,
            flatList,
            bandBefore,
            smallText,
            rowActions,
            rowNote,
            rowToggle,
            onNodeRename,
            disableOverlay,
            onSelectedComponentChange,
            expandAll
        },
        ref
    ) => {
        const {
            nodes,
            openNodes,
            selectedIds,
            overlayNodeId,
            setDraggedId,
            setOverlayNodeId,
            findNodeById,
            findParentId,
            findNodeByDataID,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setSelectedIds,
            flatVisibleList
        } = treeState;

        const treeContainerRef = useRef<HTMLDivElement>(null);

        // UN ALBERO È UNA FERMATA SOLA DEL TAB (utente, 2026-10-22, accessibilità; schema «tree» di WAI-ARIA). Ci si
        // entra sulla riga col fuoco di prima, o sulla scelta, o sulla prima; dentro ci si muove con le frecce. I comandi
        // di una riga (occhio, lucchetto, «…») sono nel giro del Tab solo per la riga col fuoco: prima si mettevano
        // in fila tutti, quattro per riga, e con un modello vero l'albero non finiva più.
        const [focusId, setFocusId] = useState<string | null>(null);
        const tabStopId = useMemo(() => {
            const shown = new Set(flatVisibleList.map((node: any) => node.id as string));
            if (focusId && shown.has(focusId)) return focusId;
            const selected = Array.from(selectedIds).find((id) => shown.has(id));
            if (selected) return selected;
            const roots = [...nodes].sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
            return (roots[0]?.id as string | undefined) ?? null;
        }, [flatVisibleList, focusId, selectedIds, nodes]);

        // Le righe si contano nell'ordine in cui sono a schermo: è quello che le frecce devono seguire.
        const focusRow = useCallback((from: HTMLElement, pick: (rows: HTMLElement[], index: number) => HTMLElement | undefined) => {
            const rows = Array.from(treeContainerRef.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? []);
            pick(rows, rows.indexOf(from))?.focus();
        }, []);

        // IL PANNELLO CONSEGNATO INVECCHIAVA, per due ragioni che si assomigliano. Quando la colonna
        // di destra è di qualcun altro (`disableOverlay`), il pannello di una riga si consegna al clic
        // e resta lì: rinominare la riga cambiava il nodo e non il pannello, e il nome nuovo si vedeva
        // solo chiudendo e riaprendo. Da qui si ripubblica ogni volta che l'albero cambia — e se ne va
        // quando la riga che descriveva non c'è più.
        //
        // L'altra ragione la copre `publishable`: si consegna la FABBRICA del pannello e non il
        // pannello, perché fra un ridisegno e l'altro cambia anche ciò di cui l'albero non si accorge,
        // cioè il gizmo che sposta l'oggetto in scena.
        const publishComponent = useRef(onSelectedComponentChange);
        publishComponent.current = onSelectedComponentChange;

        // E NON SI PUBBLICA IL VUOTO SE NON SI AVEVA NIENTE DA DIRE. La colonna di destra è UNA
        // per tutte le sezioni, ma gli alberi che ci scrivono dentro sono tanti — e non tutti sono
        // in vista: la libreria dei materiali resta montata mentre guardi gli oggetti, perché
        // trasloca invece di rinascere. Un albero fermo, senza niente di aperto, pubblicava `null`
        // a ogni ridisegno: cancellava il pannello che un'ALTRA sezione aveva appena aperto, e da
        // fuori era «clicco una riga e non succede niente».
        //
        // Qui il vuoto lo pubblica solo chi prima aveva pubblicato qualcosa — cioè chi sta davvero
        // chiudendo il proprio pannello. Chi non ha mai scritto in quella colonna non ha titolo per
        // cancellarla.
        const publishedNodeId = useRef<string | null>(null);

        useEffect(() => {
            if (!disableOverlay) return;

            const openNode = overlayNodeId ? findNodeById(overlayNodeId) : undefined;

            if (!openNode?.component) {
                // SI TIENE IL VECCHIO FINCHÉ NON ARRIVA IL NUOVO. Se qualcuno dichiara ancora di avere
                // una riga aperta ma quella riga non si trova, non è una chiusura: è una sostituzione a
                // metà strada — la luce di prima è stata rifatta e la sua erede sta per comparire.
                // Svuotare la colonna in quel varco la fa richiudere e riaprire, e la scena 3D accanto
                // si allarga e si restringe due volte per un gesto solo.
                //
                // Il vuoto lo pubblica solo chi non ha più NIENTE di aperto, cioè chi ha chiuso davvero.
                if (overlayNodeId) return;

                if (publishedNodeId.current !== null) {
                    publishedNodeId.current = null;
                    publishComponent.current?.(null);
                }
                return;
            }

            publishedNodeId.current = overlayNodeId;

            publishComponent.current?.(publishable(openNode.component));
            // `findNodeById` cambia identità esattamente quando cambia l'albero: è lui a dire
            // «qualcosa si è mosso, il pannello va rifatto».
        }, [overlayNodeId, findNodeById, disableOverlay]);

        // «DOVE STA?» — la riga si accende e si spegne tre volte, e prima di tutto si porta in vista.
        //
        // Non cambia la selezione e non apre niente: è una risposta, non un comando. Chi la chiede è
        // la freccetta nella testata dell'Inspector — sei già sul soggetto, e vuoi sapere dove sta
        // nella colonna — ma la usa anche chi arriva da un altro elenco, dopo aver selezionato.
        const [blinkingId, setBlinkingId] = useState<string | null>(null);

        // IL GESTO STA IN UN POSTO SOLO — secondo clic, Invio, Esc, uscita dal campo — ed è lo stesso
        // con cui si rinomina una tessera della griglia: `useInlineRename`.
        const rename = useInlineRename();

        // A OGNI DOMANDA SI RISPONDE UNA VOLTA SOLA, ma si può rispondere in ritardo. Arrivando da
        // un'altra sezione la domanda precede l'albero: questo componente si sta montando proprio
        // adesso e le righe non ci sono ancora. Finché la riga non si trova il gettone non si segna,
        // e l'effetto riprova a ogni cambio dell'elenco — cioè appena l'albero si riempie.
        //
        // È il pezzo che prima mancava: chiedere «dove sta» passando di sezione andava a vuoto la
        // prima volta, e funzionava alla seconda perché a quel punto la sezione era già montata.
        const servedBlink = useRef(0);

        useEffect(() => {
            if (!blinkRequest || blinkRequest.token === servedBlink.current) return;

            const found = blinkRequest.nodeId ?? findNodeByDataID(nodes, blinkRequest.dataID ?? '')?.id;
            if (!found) return;

            servedBlink.current = blinkRequest.token;
            const nodeId = found;

            // I RAMI CHIUSI SI APRONO LUNGO LA STRADA. Una riga dentro un gruppo chiuso non esiste a
            // schermo: farla lampeggiare senza aprirle la strada è rispondere «è là dentro» a chi ha
            // chiesto dove sta.
            const path: string[] = [];
            let walking: string | null = nodeId;

            while (walking) {
                const parentId: string | null = findParentId(walking);
                if (parentId) path.push(parentId);
                walking = parentId;
            }

            if (treeState.openPath) treeState.openPath(path);
            else path.filter((id) => !openNodes.has(id)).forEach((id) => toggleNode(id));

            setBlinkingId(nodeId);

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [blinkRequest, nodes]);

        // LA RIGA SI CERCA DOPO CHE C'È, non dopo un fotogramma. Aperti i rami lungo la strada, nasce nel
        // ridisegno che accende anche il lampeggio: un `requestAnimationFrame` poteva arrivare prima, e la
        // riga lampeggiava fuori vista.
        useLayoutEffect(() => {
            if (!blinkingId) return;

            const target = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(blinkingId)) : blinkingId;
            treeContainerRef.current?.querySelector(`[data-node-id="${target}"]`)?.scrollIntoView({ block: 'nearest' });
        }, [blinkingId]);

        // QUANTO DURA STA IN UN EFFETTO SUO. Insieme al gesto, il cronometro ripartiva da capo a ogni
        // cambio dell'elenco — e peggio: quando l'effetto ripartiva per una domanda già servita,
        // spegneva il cronometro senza rimetterlo, e la riga restava accesa per sempre. Il tempo
        // dipende solo da quale riga sta lampeggiando.
        useEffect(() => {
            if (!blinkingId) return;

            const timer = window.setTimeout(() => setBlinkingId(null), BLINK_DURATION);
            return () => window.clearTimeout(timer);
        }, [blinkingId]);

        // Esponi i metodi tramite useImperativeHandle
        useImperativeHandle(
            ref,
            () => ({
                closeOverlay: () => {
                    setOverlayNodeId(null);
                    onSelectedComponentChange?.(null);
                },
                // «NIENTE DI TUTTO QUESTO», chiesto da fuori. Il vuoto sotto le righe è dell'albero
                // finché l'albero riempie il pannello; sotto, il vuoto è di chi lo ospita, e un clic
                // là deve valere lo stesso — altrimenti deselezionare dipende da quante righe hai.
                clearSelection: () => {
                    setSelectedIds(new Set());
                    setOverlayNodeId(null);
                },
                // L'ORDINE CONTA: con il segno di «ho pubblicato» già tolto, l'effetto che segue la riga
                // aperta non scrive il vuoto nella colonna, che non è più sua.
                releaseOverlay: () => {
                    publishedNodeId.current = null;
                    setOverlayNodeId(null);
                },
                // APRIRE IL NOME DI UNA RIGA DA FUORI: serve a chi crea qualcosa e vuole che il nome
                // sia già in scrittura, senza far passare da una finestra.
                startRename: (nodeId: string) => {
                    const target = findNodeById(nodeId);
                    if (target) rename.start(nodeId, target.name);
                },
                selectAndOpenOverlay: (dataID: string) => {
                    // 1) Usa la ricerca ricorsiva su tutti i rami dell'albero. Vale anche l'id della
                    // riga: l'elenco di una selezione multipla conosce le righe, e una camera salvata
                    // non ha un oggetto 3D da cui farsi ritrovare.
                    const targetNode = findNodeByDataID(treeState.nodes, dataID) ?? findNodeById(dataID);

                    if (!targetNode) {
                        console.warn(`Nessun nodo trovato per meshId ${dataID}`);
                        return;
                    }

                    if (!targetNode.doNotOpenOverlay && targetNode.component) {
                        onSelectedComponentChange?.(publishable(targetNode.component));
                    }

                    // 2) Trova tutti i nodi padre che devono essere aperti
                    const nodesToOpen: string[] = [];
                    let currentId = targetNode.id;

                    while (currentId) {
                        const parentId = findParentId(currentId);
                        if (parentId) {
                            // Aggiungi solo se non è già aperto
                            if (!openNodes.has(parentId)) {
                                nodesToOpen.push(parentId);
                            } else {
                            }
                            currentId = parentId;
                        } else {
                            break;
                        }
                    }

                    // 3) Apri la strada: senza invertire i rami, che con uno stato vecchio chiuderebbe quelli aperti.
                    if (treeState.openPath) {
                        const path: string[] = [];
                        for (let parentId = findParentId(targetNode.id); parentId; parentId = findParentId(parentId)) path.push(parentId);
                        treeState.openPath(path);
                    } else nodesToOpen.forEach((nodeId) => toggleNode(nodeId));

                    // 4) Selezione
                    setSelectedIds(new Set([targetNode.id]));

                    // 5) Apertura overlay
                    if (targetNode.component && !targetNode.doNotOpenOverlay) {
                        setOverlayNodeId(targetNode.id);
                    }
                }
            }),
            // aggiungo queste dipendenze perchè ad esempio, usando openNodes nella selectAndOpenOverlay, prendendo il valore alla creazione dell'imperative,
            // openNodes restava sempre vuoto, quindi ho aggiunto ognuno per:
            // openNodes - per controllare quali nodi sono aperti
            // findNodeByDataID - per trovare il nodo target
            // findParentId - per risalire la catena dei parent
            // toggleNode - per aprire i nodi
            // setSelectedIds - per selezionare il nodo
            // setOverlayNodeId - per aprire l'overlay
            // treeState.nodes - per la ricerca nel tree
            // findNodeById - per leggere il nome da cui parte la scrittura
            [
                setOverlayNodeId,
                openNodes,
                findNodeByDataID,
                findNodeById,
                findParentId,
                toggleNode,
                setSelectedIds,
                treeState.nodes,
                onSelectedComponentChange,
                rename
            ]
        );

        // IL TRASCINAMENTO DELLE RIGHE (pointerReorder; utente, 2026-10-15). Una riga si lascia DENTRO
        // un'altra — che diventa il suo ramo, se può farlo (`canTurnIntoFolder`) — oppure DAVANTI o DOPO
        // un'altra, e allora prende il ramo di quella. Nel vuoto sotto l'ultima riga torna alla radice. Una riga scelta
        // porta con sé tutta la selezione, ognuna col suo ramo.
        //
        // IN UNA LISTA PIATTA non c'è dentro: il trascinamento dice solo dove va messa la riga, e chi ospita
        // l'albero scrive il nuovo ordine (`onNodeMoved` riceve la riga davanti a cui cadere, o `null` in coda).
        const isUnder = useCallback(
            (id: string, ancestors: string[]) => {
                for (let parent = findParentId(id); parent; parent = findParentId(parent)) if (ancestors.includes(parent)) return true;
                return false;
            },
            [findParentId]
        );

        // Il gesto dura più di un render: le regole (`canTurnIntoFolder` dei materiali guarda chi si sta
        // trascinando) si leggono dall'ultimo stato, non da quello del momento in cui è partito.
        const treeStateRef = useRef(treeState);
        treeStateRef.current = treeState;

        // DOVE CADE UNA RIGA: lo stesso per il trascinamento e per Alt+frecce o «Move up/down» (utente, 2026-10-22,
        // accessibilità: riordinare la scena si poteva fare solo trascinando).
        const applyDrop = useCallback(
            (carriedIds: string[], targetId: string, position: 'before' | 'after' | 'into', visibleIds: string[]) => {
                if (flatList) {
                    const rest = visibleIds.filter((id) => !carriedIds.includes(id));
                    const before = position === 'before' ? targetId : (rest[rest.indexOf(targetId) + 1] ?? null);
                    onNodeMoved(carriedIds[0], before, 0);
                    return;
                }
                if (!treeState.moveSelection) return;
                if (position === 'into') {
                    treeState.moveSelection(carriedIds, targetId);
                    onNodeMoved(carriedIds[0], targetId, findNodeById(targetId)?.children?.length ?? 0);
                    if (!openNodes.has(targetId)) toggleNode(targetId);
                    return;
                }
                const parentId = findParentId(targetId);
                treeState.moveSelection(carriedIds, parentId, { id: targetId, position });
                const siblings = parentId ? (findNodeById(parentId)?.children ?? []) : nodes;
                onNodeMoved(
                    carriedIds[0],
                    parentId,
                    Math.max(
                        0,
                        siblings.findIndex((node: any) => node.id === targetId)
                    )
                );
            },
            [flatList, treeState, onNodeMoved, findNodeById, openNodes, toggleNode, findParentId, nodes]
        );

        // SU E GIÙ FRA I FRATELLI, senza trascinare: Alt+↑/↓ sulla riga, o «Move up/down» nel suo «…». Si sposta la
        // selezione se la riga ne fa parte, come col trascinamento; fuori dal proprio ramo non si esce (per quello c'è
        // il trascinamento, o Group). Dove il trascinamento è spento, o lascia «su» una riga (i materiali), non c'è.
        const canMoveRows = !disableDragAndDrop && rowDropMode === 'tree' && (!!flatList || !!treeState.moveSelection);
        const byOrder = (list: any[]) => [...list].sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
        const moveTarget = useCallback(
            (nodeId: string, direction: -1 | 1) => {
                const carried = selectedIds.has(nodeId) ? Array.from(selectedIds) : [nodeId];
                const parentId = flatList ? null : findParentId(nodeId);
                const siblings = byOrder(parentId ? (findNodeById(parentId)?.children ?? []) : nodes).map((node: any) => node.id as string);
                const own = siblings.filter((id) => carried.includes(id));
                // UNA SELEZIONE SU PIÙ RAMI NON SI SPOSTA DA TASTIERA: chi ospita l'albero (objectEditor) riparenta in
                // Babylon TUTTA la selezione sotto il genitore della riga, mentre qui si spostano solo i fratelli: le
                // altre righe (anche i figli selezionati della riga) resterebbero al loro posto nell'albero e finirebbero
                // altrove nella scena. Si sposta solo una selezione fatta tutta di fratelli.
                if (!own.length || own.length !== carried.length) return null;
                const edge = siblings.indexOf(direction < 0 ? own[0] : own[own.length - 1]);
                const targetId = siblings[edge + direction];
                return targetId === undefined ? null : { carried: own, targetId, siblings };
            },
            [selectedIds, flatList, findParentId, findNodeById, nodes]
        );
        const moveRow = useCallback(
            (nodeId: string, direction: -1 | 1) => {
                const move = moveTarget(nodeId, direction);
                if (!move) return;
                applyDrop(move.carried, move.targetId, direction < 0 ? 'before' : 'after', move.siblings);
                // La riga ridisegnata è un altro elemento: il fuoco la segue.
                requestAnimationFrame(() => treeContainerRef.current?.querySelector<HTMLElement>(`[role="treeitem"][data-node-id="${nodeId}"]`)?.focus());
            },
            [moveTarget, applyDrop]
        );

        const startRowDrag = useCallback(
            (event: React.PointerEvent<HTMLElement>, draggedNodeId: string) => {
                const visibleIds = flatVisibleList.map((node: any) => node.id as string);
                const selection = Array.from(selectedIds);
                const carried = carriedFrom(visibleIds, draggedNodeId, selection);
                // Una riga dentro un'altra già portata viaggia col ramo: non si sposta due volte.
                const carriedIds = carried.filter((id) => !isUnder(id, carried));
                const blocked = (targetId: string) => carriedIds.includes(targetId) || isUnder(targetId, carriedIds);

                // IL DOM PARLA IN TESTO, L'ALBERO NO (utente, 2026-10-15). Le mesh hanno per id l'`uniqueId` di
                // Babylon, un numero; `data-reorder-id` lo restituisce come stringa, e «123» non trovava il
                // nodo 123: il genitore risultava «nessuno» e le righe lasciate in un gruppo finivano in fondo
                // alla radice. Al confine si traduce, in tutt'e due i versi.
                const realIds = new Map(visibleIds.map((id) => [String(id), id]));
                const real = (domId: string) => realIds.get(domId) ?? domId;

                startPointerReorder(event, {
                    fromRow: true,
                    draggedId: String(draggedNodeId),
                    carriedIds: carriedIds.map(String),
                    // Chi ospita l'albero legge la selezione per sapere che cosa spostare: la riga presa
                    // fuori dalla selezione diventa la selezione prima che il gesto finisca.
                    onStart: () => {
                        if (!selectedIds.has(draggedNodeId)) setSelectedIds(new Set([draggedNodeId]));
                        // Chi ospita può chiedere chi si sta trascinando (`canTurnIntoFolder` dei materiali).
                        setDraggedId(draggedNodeId);
                    },
                    onEnd: () => setDraggedId(null),
                    canDrop: (domId, position) => {
                        const targetId = real(domId);
                        const state = treeStateRef.current;
                        if (blocked(targetId)) return false;
                        if (flatList) return position !== 'into';
                        if (position !== 'into') return rowDropMode === 'tree' && !!state.moveSelection;
                        const target = state.findNodeById(targetId);
                        return !!target && !!state.moveSelection && (!state.canTurnIntoFolder || state.canTurnIntoFolder(target));
                    },
                    onDrop: (domId, position) => applyDrop(carriedIds, real(domId), position, visibleIds)
                });
            },
            [
                flatVisibleList,
                selectedIds,
                isUnder,
                setSelectedIds,
                setDraggedId,
                flatList,
                rowDropMode,
                treeState,
                findNodeById,
                onNodeMoved,
                openNodes,
                toggleNode,
                findParentId,
                nodes
            ]
        );

        // IL POSTO DELLA FRECCETTA SERVE SOLO DOVE QUALCOSA SI APRE. Una riga senza figli lo tiene vuoto
        // per allinearsi alle righe che si aprono; in un elenco piatto — le luci, le camere — nessuna
        // si apre, e lo spazio vuoto spingeva tutte le righe a destra senza allinearle a niente.
        // Basta guardare il primo livello: un ramo annidato sta per forza dentro uno del primo.
        const hasBranches = useMemo(() => nodes.some((node: any) => node.children?.length > 0), [nodes]);

        // LA SCIA: i rami che contengono almeno una riga scelta, senza contare la riga stessa.
        const trailIds = useMemo(() => {
            const trail = new Set<string>();
            if (!selectedIds.size) return trail;
            const walk = (list: any[], path: string[]) =>
                list.forEach((node: any) => {
                    if (selectedIds.has(node.id)) path.forEach((id) => trail.add(id));
                    if (node.children?.length) walk(node.children, [...path, node.id]);
                });
            walk(nodes, []);
            return trail;
        }, [nodes, selectedIds]);

        // Funzione di rendering ricorsivo
        const renderTree = useCallback(
            (node: any, depth = 0, parent: string | null = null) => {
                const isFolder = node.children?.length > 0;
                const isOpen = expandAll || openNodes.has(node.id);
                const isTrail = !selectedIds.has(node.id) && trailIds.has(node.id);

                const groupVis = isFolder ? groupState(branchOf(node), isShown) : null;
                const isVis = isFolder ? groupVis !== 'none' : node.visible !== false;
                const isMixed = groupVis === 'some';

                // UN GRUPPO È BLOCCATO SE LO SONO TUTTI I SUOI, come per l'occhio: il contenitore non
                // ha un lucchetto suo, ha il riassunto di quelli che contiene.
                const groupLock = isFolder ? groupState(leavesOf(node), isShut) : null;
                const isLocked = isFolder ? groupLock === 'all' : node.locked === true;
                const isLockMixed = groupLock === 'some';
                const isSel = selectedIds.has(node.id);

                // SU UNA RIGA SCELTA INSIEME AD ALTRE, occhio e «…» valgono per tutte (round 4,
                // decisione 3): è il gesto degli strumenti di authoring, il comando cade sulla selezione.
                const subjectIds = isSel && selectedIds.size > 1 ? Array.from(selectedIds) : [node.id];
                const isTabStop = node.id === tabStopId;
                // I comandi della riga sono nel giro del Tab solo sulla riga col fuoco.
                const commandTabIndex = isTabStop ? 0 : -1;

                // Il clic sulla riga e Invio fanno la stessa cosa.
                const activateRow = (additive: boolean, range = false) => {
                    node.onNodeClick?.();
                    toggleSelection(node.id, range, additive);

                    if (node.doNotOpenOverlay) {
                        setOverlayNodeId(null);
                        onSelectedComponentChange?.(null);
                        return;
                    }

                    if (node.component) {
                        setOverlayNodeId(node.id);
                        onSelectedComponentChange?.(publishable(node.component));
                    }
                };

                const row = (
                    <TreeNodeContainer
                        data-node-id={node.id}
                        data-reorder-id={node.id}
                        data-reorder-level={depth}
                        $depth={depth}
                        $selected={isSel}
                        $trail={isTrail}
                        $isVisible={isVis}
                        $blinking={blinkingId === node.id}
                        $smallText={smallText}
                        role='treeitem'
                        aria-selected={isSel}
                        aria-level={depth + 1}
                        aria-expanded={isFolder ? isOpen : undefined}
                        tabIndex={isTabStop ? 0 : -1}
                        onFocus={(e) => {
                            if (e.target === e.currentTarget) setFocusId(node.id);
                        }}
                        // SI TRASCINA LA RIGA INTERA (utente, 2026-10-15): la maniglia in fondo alla riga non c'è più.
                        onPointerDown={
                            disableDragAndDrop
                                ? undefined
                                : (e) => {
                                      if (rename.editingId === node.id) return;
                                      startRowDrag(e, node.id);
                                  }
                        }
                        onKeyDown={(e) => {
                            // I tasti di un comando della riga sono del comando, non della riga.
                            if (e.target !== e.currentTarget) return;
                            const row = e.currentTarget;
                            const handled = () => e.preventDefault();

                            if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                                if (!canMoveRows) return;
                                handled();
                                moveRow(node.id, e.key === 'ArrowUp' ? -1 : 1);
                                return;
                            }

                            switch (e.key) {
                                // INVIO È IL CLIC: sceglie la riga e apre il suo pannello. Prima sceglieva soltanto, e
                                // l'Inspector restava su «Nothing selected». Spazio fa lo stesso; con Ctrl/⌘ aggiunge o
                                // toglie la riga dalla scelta, come il clic.
                                case 'Enter':
                                case ' ':
                                    handled();
                                    activateRow(e.key === ' ' && (e.ctrlKey || e.metaKey));
                                    break;
                                case 'ArrowDown':
                                    handled();
                                    focusRow(row, (rows, index) => rows[index + 1]);
                                    break;
                                case 'ArrowUp':
                                    handled();
                                    focusRow(row, (rows, index) => rows[index - 1]);
                                    break;
                                case 'Home':
                                    handled();
                                    focusRow(row, (rows) => rows[0]);
                                    break;
                                case 'End':
                                    handled();
                                    focusRow(row, (rows) => rows[rows.length - 1]);
                                    break;
                                // A destra si apre il ramo, e se è già aperto si scende al primo figlio; a sinistra si
                                // chiude, e se è già chiuso si sale al genitore.
                                case 'ArrowRight':
                                    if (!isFolder) break;
                                    handled();
                                    if (!isOpen) toggleNode(node.id);
                                    else focusRow(row, (rows, index) => rows[index + 1]);
                                    break;
                                case 'ArrowLeft':
                                    handled();
                                    if (isFolder && isOpen) toggleNode(node.id);
                                    else if (parent)
                                        treeContainerRef.current?.querySelector<HTMLElement>(`[role="treeitem"][data-node-id="${parent}"]`)?.focus();
                                    break;
                                // F2 RINOMINA, come nei gestori di file: il secondo clic sul nome non ha un tasto suo.
                                case 'F2':
                                    if (!onNodeRename) break;
                                    handled();
                                    rename.start(node.id, node.name);
                                    break;
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            activateRow(e.ctrlKey || e.metaKey, e.shiftKey);
                        }}
                    >
                        {!isFolder && hasBranches && <TreeTwistySlot aria-hidden='true' />}
                        {isFolder && (
                            <TreeTwisty
                                type='button'
                                $isOpen={isOpen}
                                $trail={isTrail}
                                tabIndex={commandTabIndex}
                                aria-label={stringFormat(isOpen ? T._('Collapse {0}', 'Admin') : T._('Expand {0}', 'Admin'), node.name)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleNode(node.id);
                                }}
                            ></TreeTwisty>
                        )}

                        {node.customIcon ? (
                            <CustomIconContainer data-reorder-anchor $trail={isTrail}>
                                {node.customIcon}
                            </CustomIconContainer>
                        ) : (
                            /* Il glifo dice CHE COSA è il nodo. Se chi costruisce l'albero
                                   lo sa — l'Outliner distingue le mesh vere dai nodi di
                                   trasformazione — lo dichiara in `kind`; le altre liste non
                                   hanno quella distinzione e ricadono sulla forma dell'albero. */
                            <Icon $kind={node.kind} $trail={isTrail} data-reorder-anchor>
                                {(node.kind ?? (isFolder ? 'group' : 'mesh')) === 'group' ? ICONS.folderClosed : ICONS.file}
                            </Icon>
                        )}

                        {rename.editingId === node.id ? (
                            <NodeNameInput aria-label={T._('Name', 'Admin')} {...rename.fieldProps(node.name, (value) => onNodeRename?.(node, value))} />
                        ) : (
                            <NodeName
                                $branch={isFolder}
                                onClick={(event) => {
                                    // IL SECONDO CLIC, NON IL DOPPIO: si apre solo il nome di una riga già scelta,
                                    // e solo se è scelta da sola — con un mucchio selezionato il clic serve ancora a
                                    // ridurre la scelta a questa riga.
                                    //
                                    // E SOLO SE È LEI QUELLA CHE SI STA GUARDANDO. Quando la colonna è passata a un
                                    // altro elenco la riga resta scelta, ma il suo pannello non c'è più: lì il clic
                                    // vuol dire «fammela rivedere», e va alla riga, che il pannello lo riapre.
                                    const isShown = !node.component || node.doNotOpenOverlay || overlayNodeId === node.id;
                                    if (!onNodeRename || !isSel || selectedIds.size !== 1 || !isShown) return;
                                    event.stopPropagation();
                                    rename.start(node.id, node.name);
                                }}
                            >
                                {markMatches(node.name, highlight)}
                            </NodeName>
                        )}

                        {(() => {
                            const note = rowNote?.(node);
                            if (!note?.text) return null;

                            return (
                                <ZTooltip title={note.title} position='top'>
                                    <RowNote>{note.text}</RowNote>
                                </ZTooltip>
                            );
                        })()}

                        {(showVisibilityIcon || rowActions || rowToggle) && (
                            <RowTrailing $pinned={!isVis || isMixed || isLocked || isLockMixed || !!rowToggle?.(node)?.active}>
                                {(() => {
                                    const toggle = rowToggle?.(node);
                                    if (!toggle) return null;

                                    return (
                                        <ZTooltip title={toggle.label} position='top'>
                                            <RowToggleIcon
                                                role='button'
                                                aria-label={toggle.label}
                                                aria-pressed={toggle.active}
                                                tabIndex={commandTabIndex}
                                                onKeyDown={pressOnKey}
                                                $on={toggle.active}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggle.onClick();
                                                }}
                                            >
                                                {toggle.icon}
                                            </RowToggleIcon>
                                        </ZTooltip>
                                    );
                                })()}

                                {showLockIcon && (
                                    <VisibilityIcon
                                        role='button'
                                        aria-label={stringFormat(
                                            isFolder
                                                ? isLocked
                                                    ? T._('Unlock all in {0}', 'Admin')
                                                    : T._('Lock all in {0}', 'Admin')
                                                : isLocked
                                                  ? T._('Unlock {0}', 'Admin')
                                                  : T._('Lock {0}', 'Admin'),
                                            node.name
                                        )}
                                        aria-pressed={isLocked}
                                        tabIndex={commandTabIndex}
                                        onKeyDown={pressOnKey}
                                        $mixed={isLockMixed}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            const nextLocked = !isLocked;

                                            (isFolder ? leavesOf(node) : [node]).forEach((target: any) => {
                                                if ((target.locked === true) === nextLocked) return;

                                                onToggleNodeLock?.(target.id, nextLocked);
                                            });
                                        }}
                                    >
                                        {isLocked ? ICONS.locked : ICONS.unlocked}
                                    </VisibilityIcon>
                                )}

                                {showVisibilityIcon && (
                                    <ZTooltip
                                        title={`${(isFolder ? groupVis === 'none' : !isVis) ? (visibilityLabels?.show ?? T._('Show', 'Admin')) : (visibilityLabels?.hide ?? T._('Hide', 'Admin'))}${onIsolate ? ` · ${T._('⌥click to isolate', 'Admin')}` : ''}`}
                                        position='top'
                                    >
                                        <VisibilityIcon
                                            role='button'
                                            aria-label={`${(isFolder ? groupVis === 'none' : !isVis) ? (visibilityLabels?.show ?? T._('Show', 'Admin')) : (visibilityLabels?.hide ?? T._('Hide', 'Admin'))} ${node.name}`}
                                            tabIndex={commandTabIndex}
                                            onKeyDown={pressOnKey}
                                            $mixed={isMixed}
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                if (e.altKey && onIsolate) {
                                                    onIsolate(node.id);
                                                    return;
                                                }

                                                // Si passa lo stato VOLUTO, non quello attuale: chi
                                                // ascolta deve applicarlo, non ricalcolarlo.
                                                const nextVisible = isFolder ? groupVis === 'none' : !isVis;

                                                // UN GRUPPO SI SPEGNE SPEGNENDO CIÒ CHE CONTIENE, riga per riga:
                                                // così ogni figlio dice da sé di essere spento. Spegnendo il solo
                                                // contenitore, aprirlo avrebbe mostrato figli accesi che in scena
                                                // però non si vedono. Il contenitore però è dentro il conto: se ha
                                                // una geometria sua, restava a schermo a ramo spento.
                                                const subjects = subjectIds.length > 1 ? subjectIds.map((id) => findNodeById(id)).filter(Boolean) : [node];

                                                // Un gruppo scelto insieme a un suo figlio porterebbe il figlio due volte, e
                                                // la seconda lo ribalterebbe di nuovo: una riga si commuta una volta sola.
                                                const targets = new Map<string, any>();
                                                subjects.forEach((subject: any) =>
                                                    (subject.children?.length ? branchOf(subject) : [subject]).forEach((target: any) =>
                                                        targets.set(target.id, target)
                                                    )
                                                );

                                                targets.forEach((target: any) => {
                                                    if ((target.visible !== false) === nextVisible) return;

                                                    toggleVisibility(target.id);
                                                    onToggleNodeVisibility?.(target.id, nextVisible);
                                                });
                                            }}
                                        >
                                            {isMixed ? ICONS.eyeMixed : isVis ? ICONS.eye : ICONS.eyeOff}
                                        </VisibilityIcon>
                                    </ZTooltip>
                                )}

                                {/* Aprire il menu non cambia la selezione: il soggetto è già deciso sopra. */}
                                {rowActions && (
                                    <CommandMenu
                                        actions={[
                                            ...rowActions(node, subjectIds),
                                            ...(canMoveRows
                                                ? [
                                                      {
                                                          name: T._('Move up', 'Admin'),
                                                          tooltip: T._('Alt+Up arrow', 'Admin'),
                                                          icon: <ArrowUpwardIcon />,
                                                          kind: 'action' as const,
                                                          disabled: !moveTarget(node.id, -1),
                                                          onClick: () => moveRow(node.id, -1)
                                                      },
                                                      {
                                                          name: T._('Move down', 'Admin'),
                                                          tooltip: T._('Alt+Down arrow', 'Admin'),
                                                          icon: <ArrowDownwardIcon />,
                                                          kind: 'action' as const,
                                                          disabled: !moveTarget(node.id, 1),
                                                          onClick: () => moveRow(node.id, 1)
                                                      }
                                                  ]
                                                : [])
                                        ]}
                                        tabIndex={commandTabIndex}
                                    />
                                )}
                            </RowTrailing>
                        )}
                    </TreeNodeContainer>
                );

                const children = isFolder && isOpen && node.children && (
                    <ChildrenContainer>
                        {[...node.children]
                            .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
                            .map((child: any) => renderTree(child, depth + 1, node.id))}
                    </ChildrenContainer>
                );

                return (
                    <div key={node.id}>
                        {depth === 0 && bandBefore?.[node.id] && <TreeBand role='presentation'>{bandBefore[node.id]}</TreeBand>}
                        {row}

                        {/* SPEGNERE NON È CHIUDERE. La condizione chiedeva anche che il ramo fosse acceso,
                            e un gruppo spento smetteva di disegnare i propri figli: da fuori l'occhio
                            COLLASSAVA il ramo invece di nascondere ciò che contiene, e per rivedere cosa ci
                            fosse dentro bisognava riaccenderlo. Le righe adesso restano, spente: sono loro a
                            dire com'è la scena. */}
                        {children}
                    </div>
                );
            },
            [
                openNodes,
                selectedIds,
                trailIds,
                tabStopId,
                focusRow,
                canMoveRows,
                moveRow,
                moveTarget,
                // QUESTA FUNZIONE DISEGNA ANCHE IL LAMPEGGIO, quindi deve rinascere quando la riga accesa
                // cambia. Senza, `renderTree` resta quello di prima e continua a disegnare tutte le righe
                // spente: il lampeggio si vedeva solo per caso, quando entro quel secondo cambiava
                // qualcos'altro qui dentro — un ramo che si apriva, la sezione che cambiava. Da fuori era
                // «devo cliccare due volte».
                blinkingId,
                disableDragAndDrop,
                toggleSelection,
                toggleNode,
                toggleVisibility,
                onToggleNodeVisibility,
                visibilityLabels,
                onIsolate,
                findNodeById,
                showLockIcon,
                onToggleNodeLock,
                startRowDrag,
                setOverlayNodeId,
                highlight,
                rowActions,
                rename,
                onNodeRename,
                expandAll,
                hasBranches,
                bandBefore,
                smallText
            ]
        );

        return (
            <TreeNodeInnerContainer
                ref={treeContainerRef}
                // Un albero vuoto non è un albero: senza righe il ruolo prometterebbe figli che non ci sono.
                role={nodes.length ? 'tree' : undefined}
                aria-label={nodes.length ? treeLabel : undefined}
                data-reorder-scope=''
                /* IL VUOTO SOTTO LE RIGHE È UNA RISPOSTA: cliccarlo vuol dire «niente di tutto questo».
                   Il contenitore arriva in fondo al pannello, quindi quel vuoto c'è sempre — e finora
                   era l'unico posto dell'elenco in cui un clic non voleva dire niente.

                   SOLO IL VUOTO VERO, cioè il contenitore in persona: una riga che lasciasse passare il
                   clic non sta dicendo «niente», sta dicendo sé stessa.

                   E CHIUDE ANCHE IL PANNELLO, perché «niente di tutto questo» non ammette eccezioni:
                   lasciarlo aperto voleva dire togliere la riga dall'elenco e continuare a descriverla
                   nella colonna di destra — da fuori, l'oggetto sembrava ancora scelto. Vale per il
                   pannello aperto qui dentro e per quello consegnato all'Inspector. */
                onClick={(event) => {
                    if (event.target !== event.currentTarget || (!selectedIds.size && !overlayNodeId)) return;

                    setSelectedIds(new Set());
                    setOverlayNodeId(null);
                }}
                /* SI PRENDE TUTTO CON CTRL+A, e «tutto» è quello che l'elenco sta MOSTRANDO: dopo il
                   filtro, e senza i figli dei rami chiusi. È la regola dei gestori di file — si prende
                   ciò che si vede — e per prenderne di più si apre il ramo.

                   STA SUL CONTENITORE E NON SULLA FINESTRA: la stessa combinazione, dentro il campo di
                   ricerca, deve continuare a selezionare il testo. Il contenitore prende il fuoco anche
                   cliccando il vuoto, che è il gesto con cui si arriva qui. */
                tabIndex={-1}
                onKeyDown={(event) => {
                    if ((event.key !== 'a' && event.key !== 'A') || (!event.ctrlKey && !event.metaKey)) return;

                    event.preventDefault();
                    setSelectedIds(new Set(flatVisibleList.map((node: any) => node.id)));
                }}
            >
                {/* La COPIA prima di ordinare non è pignoleria: `sort` riordina sul posto, e questo
                    array arriva dal `useMemo` di chi ci ospita — riordinarlo qui vuol dire cambiare
                    uno stato memoizzato durante il render di un altro. */}
                {nodes.length === 0 && emptyMessage ? (
                    <SceneEmptyState icon={emptyIcon}>{emptyMessage}</SceneEmptyState>
                ) : (
                    [...nodes].sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0)).map((node: any) => renderTree(node, 0, null))
                )}

                {overlayNodeId && !disableOverlay && (
                    <OverlayPanel
                        onClose={() => {
                            setOverlayNodeId(null);
                            onSelectedComponentChange?.(null);
                        }}
                    >
                        {(() => {
                            const node = findNodeById(overlayNodeId);
                            if (!node?.component) return <div>No component selected.</div>;

                            return typeof node.component === 'function' ? node.component() : node.component;
                        })()}
                    </OverlayPanel>
                )}
            </TreeNodeInnerContainer>
        );
    }
);

export default TreeComponent;
